import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import classNames from 'classnames';
import { addMonths, eachDayOfInterval, format, subMonths } from 'date-fns';
import { Dispatch, ReactElement, SetStateAction } from 'react';

import { useStoreState } from '~/store/hooks';
import DatePickerMUI from '~/ui/components/inputs/DatePicker/DatePickerMUI';
import type { WeekRange } from '~/ui/components/inputs/DatePicker/WeekPickerMUI';
import WeekPickerMUI from '~/ui/components/inputs/DatePicker/WeekPickerMUI';
import weekRangeToCalendarState from '~/ui/pages/MedicationLogistics/helpers/weekRangeToCalendarState';

import arrowBackIcon from '~/ui/assets/images/arrowBack.svg';
import styles from './MonthControls.module.scss';

interface IProps {
  selectedMonth: Date;
  prefix?: string;
  postfix?: string;
  noClient?: boolean;
  showDatePicker?: boolean;
  showWeekPicker?: boolean;
  extendedStartDate?: Date;
  extendedEndDate?: Date;
  weekRange?: number;
  setSelectedMonth?: (date: Date) => void | Dispatch<SetStateAction<Date>>;
  setWeekRange?: Dispatch<SetStateAction<number>>;
  onWeekChange?: (range: WeekRange) => void;
}

const MonthControls = ({
  selectedMonth,
  prefix,
  postfix,
  noClient = false,
  showDatePicker = false,
  showWeekPicker = false,
  extendedStartDate,
  extendedEndDate,
  weekRange = 0,
  setSelectedMonth,
  setWeekRange,
  onWeekChange,
}: IProps): ReactElement => {
  const { current } = useStoreState(state => state.client);

  const nextMonth = () => setSelectedMonth?.(addMonths(selectedMonth, 1));
  const prevMonth = () => setSelectedMonth?.(subMonths(selectedMonth, 1));

  if (!current && !noClient) return null;

  const renderContent = () => {
    if (showDatePicker) {
      return (
        <DatePickerMUI
          value={selectedMonth}
          views={['month', 'year']}
          size="small"
          className={styles.datePicker}
          dateFormat="MMM yyyy"
          fullWidth={false}
          showIcon={false}
          onChange={val => setSelectedMonth?.(new Date(val))}
        />
      );
    }
    if (showWeekPicker && extendedStartDate && extendedEndDate) {
      const handleWeekChange = (range: WeekRange) => {
        if (onWeekChange) {
          onWeekChange(range);
        } else {
          const { selectedMonth: newMonth, weekRange: newWeekRange } =
            weekRangeToCalendarState(range);
          setSelectedMonth?.(newMonth);
          setWeekRange?.(newWeekRange);
        }
      };
      const daysInterval = eachDayOfInterval({
        start: extendedStartDate,
        end: extendedEndDate,
      });
      const startIdx = 7 * weekRange;
      const endIdx = Math.min(startIdx + 6, daysInterval.length - 1);
      const selectedWeek: WeekRange = [daysInterval[startIdx], daysInterval[endIdx]];
      const monthStr = format(selectedMonth, 'MMM yyyy');
      const prefixStr = prefix ? `${prefix} - ` : '';
      const postfixStr = postfix ? ` - ${postfix}` : '';
      const displayValue = `${prefixStr}${monthStr}${postfixStr}`;
      return (
        <WeekPickerMUI
          value={selectedWeek}
          onChange={handleWeekChange}
          displayValue={displayValue}
          showIcon={false}
          fullWidth={false}
          weekStartsOn={0}
          className={styles.weekPicker}
          size="small"
        />
      );
    }

    return (
      <>
        {prefix ? `${prefix} - ` : ''} {format(selectedMonth, 'MMM')}{' '}
        {postfix ? ` - ${postfix}` : ''}
        <div className={styles.label}>{format(selectedMonth, 'YYY')}</div>
      </>
    );
  };

  return (
    <Box className={styles.monthControls}>
      <IconButton onClick={prevMonth}>
        <img src={arrowBackIcon} alt="Previous month" />
      </IconButton>
      <div className={styles.labelRow}>
        <div className={classNames(styles.label, styles.bold)}>{renderContent()}</div>
      </div>
      <IconButton onClick={nextMonth}>
        <img src={arrowBackIcon} alt="Next month" className={styles.reflected} />
      </IconButton>
    </Box>
  );
};

export default MonthControls;
